/**
 * @file Purchase Album Checkout component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StripeCheckout from 'react-stripe-checkout';
import get from 'lodash.get';
import { Redirect, Link } from 'react-router-dom';

import {
  allowsJoinWithoutPurchase,
  getCheckoutItemsForJoin,
  getCheckoutItemsForPurchase,
  getAlbumPrice,
} from '../../util/purchase-album';
import PurchaseAlbumHeaderContainer from '../../containers/purchase/PurchaseAlbumHeaderContainer';
import Button from '../global/Button';
import ContentWrap from '../global/ContentWrap';
import IdentityAvatarContainer from '../../containers/global/identity/IdentityAvatarContainer';
import Alert from '../global/Alert';
import { formatPriceForDisplay } from '../../util/format-money';
import { getAsset } from '../../util/assets';
import getConfig from '../../util/config';
import AlbumFolderCheckoutUpsell from './AlbumFolderCheckoutUpsell';
import ContextualString from '../contextual/ContextualString';
import { createAlbumRequiresPurchase, isPpa, isPpd } from '../../util/album';
import CustomControl from '../global/CustomControl';
import StopIcon from '../icons/StopIcon';
import { AVATAR_SIZE_36 } from '../../constants/avatar-sizes';
import PurchasingOptionsInfoInterstitial from '../../containers/purchase/PurchasingOptionsInfoInterstitial';
import {
  ACCOUNT_UPDATE_PHONE_NUMBER_MODAL,
  ALBUM_FOLDER_PURCHASE_OPTIONS_INTERSTITIAL_MODAL,
} from '../../constants/modals';
import InfoCircleIcon from '../icons/InfoCircleIcon';
import PricedButton from '../global/PricedButton';
import AccountUpdatePhoneNumberModalContainer from '../../containers/auth/AccountUpdatePhoneNumberModalContainer';

class PurchaseAlbumCheckout extends Component {
  static propTypes = {
    account: PropTypes.shape({
      id: PropTypes.string,
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      is_demo: PropTypes.bool.isRequired,
      subscription_album_folder: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
      }),
      creator: PropTypes.shape({
        organization_name: PropTypes.string,
      }),
    }),
    selectedIdentitiesForPurchase: PropTypes.arrayOf(PropTypes.object),
    selectedIdentitiesForJoin: PropTypes.arrayOf(PropTypes.object),
    onCheckout: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    success: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    match: PropTypes.shape({
      url: PropTypes.string,
    }),
    price: PropTypes.shape({
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      shipping_cost: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
      tax: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
    selectIdentityForPurchase: PropTypes.func,
    deselectIdentityForPurchase: PropTypes.func,
    selectIdentityForJoin: PropTypes.func,
    deselectIdentityForJoin: PropTypes.func,
    loadingPrice: PropTypes.bool,
    joinedIdentities: PropTypes.arrayOf(PropTypes.object),
    requestConfirm: PropTypes.func,
    openModal: PropTypes.func,
  };

  componentDidMount() {
    const { album } = this.props;

    // Albums that require purchase to create
    // are free for the identities to join (they work
    // kind of like pre-paid), which is why we will be avoiding
    // the checkout view all together.
    if (createAlbumRequiresPurchase(get(album, 'album_subtype'))) {
      this.onCheckout();
    }
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { success, history, match } = nextProps;

    if (success) {
      history.push(`${match.url}/success`);
    }
  }

  onAlbumFolderUpsellConfirm = () => {
    const {
      album,
      selectedIdentitiesForJoin,
      deselectIdentityForJoin,
      selectIdentityForPurchase,
    } = this.props;

    const albumFolder = album.subscription_album_folder;
    if (!albumFolder) {
      return;
    }

    // Select all of them for purchase
    selectedIdentitiesForJoin.forEach(identity => {
      deselectIdentityForJoin(identity);
      selectIdentityForPurchase(identity);
    });
  };

  onCheckout = (stripeToken = {}) => {
    const {
      account,
      onCheckout,
      selectedIdentitiesForPurchase,
      selectedIdentitiesForJoin,
      album,
      openModal,
    } = this.props;

    const joinItems = getCheckoutItemsForJoin(
      selectedIdentitiesForPurchase,
      selectedIdentitiesForJoin,
      album,
    );

    const purchaseItems = getCheckoutItemsForPurchase(
      selectedIdentitiesForPurchase,
      album,
    );

    // TODO: When the purchase-album reducer gets refactored
    // to use a single object of selected identities with the JOIN_TYPE
    // enum convert this method to use `requiresValidPhoneToCompleteCheckout`
    // instead.
    // TODO: [EMAIL_ONBOARD] Remove forced ignore after proper QA. If we end up going forward with this remove block all together
    if (false && !account.phone_number && joinItems.length > 0) {
      openModal(ACCOUNT_UPDATE_PHONE_NUMBER_MODAL, {
        onAccountUpdated: () => {
          this.onCheckout(stripeToken);
        },
      });
      return;
    }

    onCheckout({
      accountId: account.id,
      email: stripeToken.email,
      stripeToken: stripeToken.id,
      joinItems,
      purchaseItems,
      stripePaymentMethodId: stripeToken.stripe_payment_method_id,
      stripeConnectAccountId: stripeToken.stripe_connect_account_id,
    });
  };

  showFolderSubscriptionUpsell = () => {
    const { album, selectedIdentitiesForJoin } = this.props;

    return !!(
      album.subscription_album_folder && selectedIdentitiesForJoin.length > 0
    );
  };

  renderEmptySelectedIdentities() {
    const { album } = this.props;

    return (
      <div className="text-center pt-1 pb-1">
        <p className="text-dark text-md">
          Looks like you haven&rsquo;t selected any faces for purchase yet.
        </p>

        <Link to={`/purchase/album/${album.id}/faces`}>
          Click here to select or create new faces for checkout.
        </Link>
      </div>
    );
  }

  renderSelectedIdentity(identity) {
    const {
      selectedIdentitiesForPurchase,
      selectIdentityForJoin,
      selectIdentityForPurchase,
      deselectIdentityForPurchase,
      deselectIdentityForJoin,
      joinedIdentities,
      album,
      requestConfirm,
    } = this.props;

    const isJoined = !!joinedIdentities.find(
      selectedIdentity => get(selectedIdentity, 'identity.id') === identity.id,
    );

    const isForPurchase = !!selectedIdentitiesForPurchase.find(
      purchasedIdentity => purchasedIdentity.id === identity.id,
    );

    return (
      <tr key={identity.id} className="text-xs">
        <td className="d-flex text-left flex-column align-items-center">
          <IdentityAvatarContainer size={AVATAR_SIZE_36} identity={identity} />
          <span className="text-dark text-bold text-center d-flex align-items-center mt-1">
            {identity.name}
          </span>
        </td>
        {isPpa(album) && (
          <td>
            <CustomControl
              id={`${identity.id}-purchase`}
              type="radio"
              name={`${identity.id}-purchase-join`}
              checked={isForPurchase}
              labelSrOnly
              onChange={() => {
                deselectIdentityForJoin(identity);
                selectIdentityForPurchase(identity);
              }}
              label="Photo Mode"
              size="md"
              className="d-inline"
            />
          </td>
        )}
        {allowsJoinWithoutPurchase(album) && (
          <td>
            <CustomControl
              id={`${identity.id}-join`}
              type="radio"
              disabled={isJoined}
              name={`${identity.id}-purchase-join`}
              labelSrOnly
              checked={!isForPurchase}
              onChange={() => {
                if (isForPurchase) {
                  requestConfirm({
                    message:
                      'By removing the subscription you will still receive notifications and can purchase photos individually.',
                    confirmText: 'Remove',
                    onConfirm: async () => {
                      deselectIdentityForPurchase(identity);
                      selectIdentityForJoin(identity);
                    },
                    onCancel: () => {},
                    confirmBtnStyle: 'danger',
                  });
                }
              }}
              label="Photo Mode"
              size="md"
              className="d-inline"
            />
          </td>
        )}
        <td>
          <Button
            btnStyle="reset"
            onClick={() => deselectIdentityForJoin(identity)}
            className="px-1"
          >
            <StopIcon size="14" color="#646e71" />
          </Button>
        </td>
      </tr>
    );
  }

  render() {
    const {
      album,
      selectedIdentitiesForJoin,
      selectedIdentitiesForPurchase,
      loading,
      error,
      price,
      loadingPrice,
      openModal,
      match,
      success,
    } = this.props;

    const selectedIdentities = [
      ...selectedIdentitiesForPurchase,
      ...selectedIdentitiesForJoin.filter(
        identity =>
          !selectedIdentitiesForPurchase.find(({ id }) => id === identity.id),
      ),
    ].sort((a, b) => a.name.localeCompare(b.name));

    const config = getConfig();
    const totalPrice = get(price, 'total_price.value', 0);
    const displayPrice = formatPriceForDisplay(totalPrice);

    if (loading || !album) {
      return (
        <ContentWrap className="text-center">
          <span className="spinner" />
        </ContentWrap>
      );
    }

    if (success) {
      return <Redirect to={`${match.url}/success`} />;
    }

    if (selectedIdentities.length === 0) {
      return <Redirect to={`/purchase/album/${album.id}/faces`} />;
    }

    return (
      <div className="wrapper-main content-wrap-gray">
        <PurchaseAlbumHeaderContainer album={album} />

        {loading ? (
          <ContentWrap className="text-center">
            <span className="spinner" />
          </ContentWrap>
        ) : (
          <ContentWrap>
            {error && <Alert type="danger">{error}</Alert>}

            {album.creator && album.is_demo && (
              <Alert type="info">
                <ContextualString
                  id="prePaidSingleAlbumCheckoutMessage"
                  type={album.album_type}
                  subtype={album.album_subtype}
                  defaultMessage="Your subscription has been pre-paid for you by {name}."
                  values={{ name: album.creator.organization_name }}
                />
              </Alert>
            )}
            {selectedIdentities.length === 0 &&
              this.renderEmptySelectedIdentities()}
            {selectedIdentities.length > 0 && (
              <table className="table table-striped mb-3 table-sm">
                <thead className="text-bold text-xs">
                  <tr>
                    <td className="text-left">&nbsp;</td>
                    {isPpa(album) && (
                      <td id="purchase-title">
                        <span className="text-bold text-dark">Photo Pass</span>
                        <span className="text-faded d-block">
                          ${formatPriceForDisplay(getAlbumPrice(album))} /
                          person
                        </span>
                      </td>
                    )}
                    {allowsJoinWithoutPurchase(album) && (
                      <td id="join-title">
                        <span className="text-bold text-dark">
                          Preview Only
                        </span>
                        <span className="text-faded d-block">
                          Pay per photo
                        </span>
                      </td>
                    )}
                    <td className="text-left">&nbsp;</td>
                  </tr>
                </thead>
                <tbody>
                  {(selectedIdentities || []).map(selectedIdentity =>
                    this.renderSelectedIdentity(selectedIdentity),
                  )}
                </tbody>
              </table>
            )}

            {selectedIdentities.length > 0 && totalPrice > 0 && (
              <div className="d-flex lh-0 text-md pt-3">
                <span className="mr-1">Total Price</span>

                <span className="ml-auto text-bold text-dark">
                  ${displayPrice}
                </span>
              </div>
            )}
            {isPpa(album) && isPpd(album) && selectedIdentities.length > 0 && (
              <div className="d-flex mt-3">
                <Button
                  btnSize="xs"
                  btnStyle="secondary"
                  onClick={() =>
                    openModal(ALBUM_FOLDER_PURCHASE_OPTIONS_INTERSTITIAL_MODAL)
                  }
                  className="ml-auto"
                >
                  <InfoCircleIcon size={12} color="#646e71" className="mr-1" />{' '}
                  Help me choose
                </Button>
              </div>
            )}

            <hr className="mt-3 mb-4" />

            {this.showFolderSubscriptionUpsell() && (
              <AlbumFolderCheckoutUpsell
                albumFolder={album.subscription_album_folder}
                onUpsellClick={this.onAlbumFolderUpsellConfirm}
                className="block-mobile-bleed border-radius-sm-0 mb-3"
              />
            )}

            {(totalPrice === 0 || loadingPrice) && (
              <Button
                disabled={
                  selectedIdentities.length === 0 || loading || loadingPrice
                }
                btnBlock
                onClick={this.onCheckout}
              >
                Find My Photos!
              </Button>
            )}

            {totalPrice > 0 && !loadingPrice && (
              <StripeCheckout
                name="Waldo Photos"
                description="Album Checkout"
                image={getAsset('/waldo-stripe-logo.png')}
                token={this.onCheckout}
                panelLabel={`Pay $${displayPrice}`}
                stripeKey={config.stripe.key}
                currency="USD"
                ComponentClass="div"
                zipCode
              >
                <PricedButton
                  disabled={selectedIdentities.length === 0 || loading}
                  btnBlock
                  price={totalPrice}
                >
                  <div className="d-flex flex-column">
                    <span>
                      {selectedIdentitiesForPurchase.length} Photo Pass
                    </span>
                    {selectedIdentities.length >
                      selectedIdentitiesForPurchase.length && (
                      <span className="text-faded text-xs">
                        {selectedIdentities.length -
                          selectedIdentitiesForPurchase.length}{' '}
                        Preview Only members
                      </span>
                    )}
                  </div>
                </PricedButton>
              </StripeCheckout>
            )}

            {selectedIdentities.length > 0 && (
              <Button
                btnStyle="secondary"
                btnBlock
                className="mt-1"
                to={`/purchase/album/${album.id}/faces`}
              >
                Go Back
              </Button>
            )}
            <PurchasingOptionsInfoInterstitial />
          </ContentWrap>
        )}

        <AccountUpdatePhoneNumberModalContainer />
      </div>
    );
  }
}

export default PurchaseAlbumCheckout;
